import { useRef, useState, useEffect } from 'react';

// ported into hook-form from react-switch
// https://github.com/markusenglund/react-switch/blob/master/src/index.jsx

const useDragSwitch = _ref => {
  let {
    checked = false,
    rtl = false,
    onChange = () => {}
  } = _ref;
  const translationLength = 18;
  const checkedPos = rtl ? translationLength * -1 : translationLength;
  const uncheckedPos = 0;
  const initialState = {
    pos: checked ? checkedPos : uncheckedPos,
    startX: 0,
    dragStartingTime: 0,
    isDragging: false
  };
  const inputRef = useRef(null);
  const timerRef = useRef(-1);
  const fromDragEvent = useRef(false);
  const lastDragAt = useRef(0);
  const [state, setState] = useState(initialState);
  const mergeState = updates => {
    setState(current => ({
      ...current,
      ...updates
    }));
  };
  useEffect(() => {
    const pos = checked ? checkedPos : uncheckedPos;
    mergeState({
      pos
    });
    return () => {
      window.clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);
  function onDragStart(clientX) {
    inputRef.current?.focus();
    mergeState({
      startX: clientX,
      dragStartingTime: Date.now()
    });
  }
  function onDrag(clientX) {
    setState(currentState => {
      const updates = {
        ...currentState
      };
      const {
        startX,
        isDragging,
        pos
      } = currentState;
      const startPos = checked ? checkedPos : uncheckedPos;
      const mousePos = startPos + clientX - startX;
      if (!isDragging && clientX !== startX) {
        updates.isDragging = true;
      }
      const newPos = rtl ? Math.max(checkedPos, Math.min(uncheckedPos, mousePos)) : Math.min(checkedPos, Math.max(uncheckedPos, mousePos));
      if (newPos !== pos) {
        updates.pos = newPos;
      }
      return updates;
    });
  }
  function onDragStop() {
    setState(currentState => {
      const {
        pos,
        isDragging,
        dragStartingTime
      } = currentState;
      const halfwayCheckpoint = (checkedPos + uncheckedPos) / 2;
      const pastHalfwayCheckpoint = Math.abs(pos) > Math.abs(halfwayCheckpoint);
      const updates = {
        ...currentState,
        isDragging: false
      };
      lastDragAt.current = Date.now();
      const timeSinceStart = Date.now() - dragStartingTime;
      if (!isDragging || timeSinceStart < 250) {
        onDragChange();
      } else if (checked) {
        if (pastHalfwayCheckpoint) {
          updates.pos = checkedPos;
        } else {
          onDragChange();
        }
      } else if (!pastHalfwayCheckpoint) {
        updates.pos = uncheckedPos;
      } else {
        onDragChange();
      }
      return updates;
    });
  }
  function onMouseDown(e) {
    e.preventDefault();
    // Ignore right click and scroll
    if (typeof e.button === "number" && e.button !== 0) {
      return;
    }
    onDragStart(e.clientX);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }
  function onMouseMove(e) {
    e.preventDefault();
    onDrag(e.clientX);
  }
  function onMouseUp() {
    onDragStop();
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  }
  function onTouchStart(e) {
    onDragStart(e.touches[0].clientX);
  }
  function onTouchMove(e) {
    onDrag(e.touches[0].clientX);
  }
  function onTouchEnd(e) {
    e.preventDefault();
    onDragStop();
  }
  function onInputChange(e) {
    if (fromDragEvent.current || Date.now() - lastDragAt.current > 50) {
      onChange(e);
      fromDragEvent.current = false;
    }
  }
  function onDragChange() {
    const event = new MouseEvent("click", {
      bubbles: true
    });
    fromDragEvent.current = true;
    lastDragAt.current = Date.now();
    timerRef.current = window.setTimeout(() => inputRef.current?.dispatchEvent(event), 0);
  }
  return {
    pos: state.pos,
    inputRef,
    onMouseDown,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onInputChange
  };
};

export { useDragSwitch as default };
